import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalModal } from "../../../global/GlobalModal";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalButton, GlobalTextField } from "../../../global/forms/GlobalFormFields";
import {
  getVolunteerMembershipsList,
  getVolunteerRenewalStats,
  updateVolunteerMembershipApplication,
} from "../api/membershipVolunteerApi";
import MembershipListMetricsSection from "../components/membershiplist/sections/MembershipListMetricsSection";
import MembershipListDirectorySection from "../components/membershiplist/sections/MembershipListDirectorySection";
import { MEMBERSHIP_LIST_COLUMN_DEFS, MEMBERSHIP_LIST_PAGE_SIZE } from "../components/membershiplist/membershipListConstants";
import {
  createInitialMembershipColumnVisibility,
  formatDateCell,
  formatMoney,
  isTruthy,
  membershipSortValue,
} from "../components/membershiplist/membershipListHelpers";
import { useMembershipRole } from "../utils/useMembershipRole";
import { MEMBERSHIPS_LIST_SUBMODULE_SLUG } from "../utils/membershipPermissionSlugs";
import "../styles/VolunteerTable.css";
import "../styles/membershipList.css";

export default function MembershipsListPage() {
  const perms = useMembershipRole(MEMBERSHIPS_LIST_SUBMODULE_SLUG);
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [filterMeta, setFilterMeta] = useState({ memberships: [], statuses: [] });
  const filterMetaRef = useRef(filterMeta);
  useEffect(() => {
    filterMetaRef.current = filterMeta;
  }, [filterMeta]);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const initialFilters = {
    student_search: "",
    column: "",
    value: "",
  };
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [sortColumn, setSortColumn] = useState("submitted_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [columnVisibility, setColumnVisibility] = useState(createInitialMembershipColumnVisibility);

  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [editForm, setEditForm] = useState({
    student_name: "",
  });
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAppliedFilters({ ...draftFilters }), 350);
    return () => clearTimeout(t);
  }, [draftFilters]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const fm = filterMetaRef.current || { memberships: [], statuses: [] };
      const queryFilters = {
        student_search: appliedFilters.student_search,
        sort_by: sortColumn,
        order: sortOrder,
        page,
        limit: MEMBERSHIP_LIST_PAGE_SIZE,
      };
      if (appliedFilters.column && appliedFilters.value) {
        if (appliedFilters.column === "membership_name") {
          const match = (fm.memberships || []).find(
            (m) => String(m.membership_name) === String(appliedFilters.value),
          );
          if (match?.id) queryFilters.membership_id = String(match.id);
        } else if (appliedFilters.column === "status_name") {
          const match = (fm.statuses || []).find(
            (s) => String(s.status_name) === String(appliedFilters.value),
          );
          if (match?.id) queryFilters.status_id = String(match.id);
        } else if (appliedFilters.column === "application_verified") {
          queryFilters.application_verified = appliedFilters.value === "Verified" ? "1" : "0";
        } else if (appliedFilters.column === "student_name" || appliedFilters.column === "student_code") {
          queryFilters.student_search = String(appliedFilters.value || "");
        }
      }
      const [res, renewalStatsRes] = await Promise.all([
        getVolunteerMembershipsList({
          ...queryFilters,
        }),
        getVolunteerRenewalStats(),
      ]);
      setRows(Array.isArray(res.data) ? res.data : []);
      setMetrics(renewalStatsRes.metrics || {});
      setFilterMeta(res.filters || { memberships: [], statuses: [] });
      setTotal(Number(res.meta?.total || 0));
      setTotalPages(Math.max(1, Number(res.meta?.totalPages || 1)));
      setPage(Number(res.meta?.page || 1));
    } catch (e) {
      setErrorMsg(e.message || "Could not load membership applications.");
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, sortColumn, sortOrder, page]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setPage(1);
  }, [appliedFilters, sortColumn, sortOrder]);

  const visibleColumns = useMemo(
    () => MEMBERSHIP_LIST_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const sortedRows = useMemo(() => {
    const mult = sortOrder === "ASC" ? 1 : -1;
    const list = [...rows];
    list.sort((a, b) => {
      const va = membershipSortValue(a, sortColumn);
      const vb = membershipSortValue(b, sortColumn);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mult;
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" }) * mult;
    });
    return list;
  }, [rows, sortColumn, sortOrder]);

  const columnFilterOptions = useMemo(
    () => MEMBERSHIP_LIST_COLUMN_DEFS.map((c) => ({ value: c.id, label: c.label })),
    [],
  );

  const valueFilterOptions = useMemo(() => {
    if (!draftFilters.column) return [];
    const seen = new Set();
    const opts = [];
    for (const row of rows) {
      let label = "";
      if (draftFilters.column === "application_verified") {
        label = isTruthy(row.application_verified) ? "Verified" : "Not verified";
      } else {
        const raw = row[draftFilters.column];
        if (raw == null || raw === "") continue;
        label = String(raw);
      }
      if (!seen.has(label)) {
        seen.add(label);
        opts.push({ value: label, label });
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
  }, [draftFilters.column, rows]);

  const metricItems = useMemo(
    () => [
      {
        key: "expiring-today",
        label: "Expiring today",
        value: Number(metrics.expiring_today_count || 0),
        tone: "coral",
      },
      {
        key: "expiring-5",
        label: "Expiring within 5 days",
        value: Number(metrics.expiring_within_5_days_count || 0),
        tone: "teal",
      },
      {
        key: "renewed-amount-today",
        label: "Renewed amount today (my collections)",
        value: `₹${Number(metrics.renewed_amount_today || 0).toFixed(2)}`,
        tone: "mint",
      },
    ],
    [metrics],
  );

  const handleSortHeaderClick = (col) => {
    if (!col.sortable) return;
    if (sortColumn === col.id) setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    else {
      setSortColumn(col.id);
      setSortOrder("ASC");
    }
  };

  const openEditModal = (row) => {
    if (!perms.canEdit) return;
    setEditErrors({});
    setEditRow(row);
    setEditForm({
      student_name: row.student_name || "",
    });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const errors = {};
    if (!String(editForm.student_name || "").trim() || String(editForm.student_name || "").trim().length < 4) {
      errors.student_name = "Student name must be at least 4 characters";
    }
    if (Object.keys(errors).length) {
      setEditErrors(errors);
      return;
    }

    setSaving(true);
    setErrorMsg("");
    try {
      await updateVolunteerMembershipApplication(editRow.application_id, {
        student_name: String(editForm.student_name || "").trim(),
      });
      setSuccessMsg("Student name updated.");
      setEditRow(null);
      await loadList();
    } catch (e) {
      setErrorMsg(e.message || "Failed to update application.");
      setEditErrors(e.details || {});
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vml-page">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <header className="vml-hero">
        <p className="vml-hero__eyebrow">Memberships</p>
        <h1 className="vml-hero__title">Applied memberships list</h1>
      </header>

      <GlobalFeedback type="error" text={errorMsg} />

      <MembershipListMetricsSection items={metricItems} loading={loading} />

      <MembershipListDirectorySection
        draftFilters={draftFilters}
        setDraftFilters={setDraftFilters}
        columnFilterOptions={columnFilterOptions}
        valueFilterOptions={valueFilterOptions}
        columnVisibility={columnVisibility}
        onVisibilityChange={setColumnVisibility}
        visibleColumns={visibleColumns}
        sortedRows={sortedRows}
        loading={loading}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSortHeaderClick={handleSortHeaderClick}
        page={page}
        totalPages={totalPages}
        total={total}
        onPageChange={setPage}
        onViewRow={setViewRow}
        onEditRow={perms.canEdit ? openEditModal : undefined}
      />

      <GlobalModal
        open={!!editRow}
        onClose={() => setEditRow(null)}
        title="Edit membership application"
        description={editRow ? `${editRow.student_name || ""} · ${editRow.application_code || ""}` : ""}
        size="lg"
      >
        {editRow ? (
          <div className="vml-edit-grid">
            <GlobalTextField label="Application code" value={editRow.application_code || ""} disabled />
            <GlobalTextField
              label="Student name"
              value={editForm.student_name}
              onChange={(v) => setEditForm((f) => ({ ...f, student_name: v }))}
              error={editErrors.student_name}
            />
            <GlobalTextField
              label="Application verified"
              value={isTruthy(editRow.application_verified) ? "Verified" : "Not verified"}
              disabled
            />
            <div className="vml-edit-actions">
              <GlobalButton type="button" variant="ghost" onClick={() => setEditRow(null)}>
                Cancel
              </GlobalButton>
              <GlobalButton type="button" variant="primary" disabled={saving} onClick={saveEdit}>
                {saving ? "Saving..." : "Save"}
              </GlobalButton>
            </div>
          </div>
        ) : null}
      </GlobalModal>

      <GlobalModal
        open={!!viewRow}
        onClose={() => setViewRow(null)}
        title="Application Overview"
        description={viewRow ? `${viewRow.student_name || ""} · ${viewRow.application_code || ""}` : ""}
        size="lg"
      >
        {viewRow ? (
          <div className="vml-view-grid">
            <div className="vml-view-card">
              <p className="vml-view-card__label">Student</p>
              <p className="vml-view-card__value">{viewRow.student_name || "—"}</p>
            </div>
            <div className="vml-view-card">
              <p className="vml-view-card__label">Student code</p>
              <p className="vml-view-card__value">{viewRow.student_code || "—"}</p>
            </div>
            <div className="vml-view-card">
              <p className="vml-view-card__label">Membership</p>
              <p className="vml-view-card__value">{viewRow.membership_name || "—"}</p>
            </div>
            <div className="vml-view-card">
              <p className="vml-view-card__label">Status</p>
              <p className="vml-view-card__value">{viewRow.status_name || "—"}</p>
            </div>
            <div className="vml-view-card">
              <p className="vml-view-card__label">Start date</p>
              <p className="vml-view-card__value">{formatDateCell(viewRow.start_date)}</p>
            </div>
            <div className="vml-view-card">
              <p className="vml-view-card__label">End date</p>
              <p className="vml-view-card__value">{formatDateCell(viewRow.end_date)}</p>
            </div>
            <div className="vml-view-card">
              <p className="vml-view-card__label">Amount</p>
              <p className="vml-view-card__value">{formatMoney(viewRow.amount)}</p>
            </div>
            <div className="vml-view-card">
              <p className="vml-view-card__label">Application verified</p>
              <p className="vml-view-card__value">{isTruthy(viewRow.application_verified) ? "Verified" : "Not verified"}</p>
            </div>
            <div className="vml-view-card">
              <p className="vml-view-card__label">Payment verified</p>
              <p className="vml-view-card__value">{isTruthy(viewRow.payment_verified) ? "Verified" : "Not verified"}</p>
            </div>
            <div className="vml-view-card vml-view-card--full">
              <p className="vml-view-card__label">Remarks</p>
              <p className="vml-view-card__value">{viewRow.remarks || "—"}</p>
            </div>
          </div>
        ) : null}
      </GlobalModal>
    </div>
  );
}
